import React, { useContext, useState, useEffect, useMemo } from 'react';
import './Payment.css';
import { PaymentContext } from "../../context/PaymentContext.js";
import { useLocation, useNavigate } from 'react-router-dom';
import Modal from 'react-modal';
import Loader from '../../components/Loader.js';
import { useToast } from '../../context/ToastContext.js';
import { AuthContext } from '../../context/AuthContext.js';
import { validateTCKN } from '../../lib/validators.js';
import countryCodesData from '../../constants/countryCodes.json';
import CountrySelector from '../../components/CountrySelector';
import { getApiUrl } from '../../config';

Modal.setAppElement('#root');

const EftPayment = () => {
  const {
    processPayment,
    confirmEftPayment
  } = useContext(PaymentContext);

  const location = useLocation();
  const { total, customerForm: initialCustomerForm, cart } = location.state || {};
  const [customerForm, setCustomerForm] = useState(initialCustomerForm || {});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { showToast } = useToast();

  // Edit mode states
  const [isEditingInvoice, setIsEditingInvoice] = useState(false);
  const [editedInvoiceData, setEditedInvoiceData] = useState({});

  // Country selector state
  const countryCodes = useMemo(() => {
    const turkey = countryCodesData.find(c => c.code === 'TR');
    const others = countryCodesData.filter(c => c.code !== 'TR').sort((a, b) => a.name.localeCompare(b.name));
    return [turkey, ...others];
  }, []);

  const [selectedCountry, setSelectedCountry] = useState(countryCodes[0]);

  // EFT/Havale ödemesi için state'ler
  const [orderCreated, setOrderCreated] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [orderAmount, setOrderAmount] = useState(0);
  const [copySuccess, setCopySuccess] = useState('');
  const [eftData, setEftData] = useState(null);

  const [isPaymentCompleteModalOpen, setIsPaymentCompleteModalOpen] = useState(false);
  const authContext = useContext(AuthContext);
  const isLoggedIn = useMemo(() => {
    const contextToken = authContext?.token;
    return Boolean(contextToken || localStorage.getItem('token'));
  }, [authContext?.token]);

  // Kullanıcı bakiyesi ve ödeme dağılımı state'leri
  const [balanceInfo, setBalanceInfo] = useState(null);
  const [paymentDistribution, setPaymentDistribution] = useState(null);

  const invoiceFieldMapping = useMemo(() => ({
    ad: 'faturaAd',
    soyad: 'faturaSoyad',
    tckimlik: 'faturaTckimlik',
    telefon: 'faturaTelefon',
    dogumTarihi: 'faturaDogumTarihi',
    adres: 'faturaAdres',
    email: 'faturaEmail'
  }), []);

  const formatPhoneForDisplay = (number = '') => {
    const digits = number.replace(/\D/g, '');
    if (digits.length !== 11) return digits || '-';
    return digits.replace(/(\d{4})(\d{3})(\d{2})(\d{2})/, '$1 $2 $3 $4');
  };

  const formatDateForDisplay = (value = '') => {
    if (!value) return '-';

    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split('-');
      return `${day}.${month}.${year}`;
    }

    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      const day = `${parsed.getDate()}`.padStart(2, '0');
      const month = `${parsed.getMonth() + 1}`.padStart(2, '0');
      const year = `${parsed.getFullYear()}`;
      return `${day}.${month}.${year}`;
    }

    return value;
  };

  const billingInfo = useMemo(() => {
    if (!customerForm) return {};

    const billingAddress =
      customerForm.faturaAdresi ||
      (customerForm.faturamAyniAdres ? customerForm.teslimatAdresi : null);

    const resolvedAddress = billingAddress || customerForm.teslimatAdresi || {};

    return {
      ad: customerForm.faturaAd ?? resolvedAddress.ad ?? customerForm.ad ?? '',
      soyad: customerForm.faturaSoyad ?? resolvedAddress.soyad ?? customerForm.soyad ?? '',
      tckimlik: customerForm.faturaTckimlik ?? resolvedAddress.tc ?? '',
      telefon: customerForm.faturaTelefon ?? resolvedAddress.telefon ?? customerForm.telefon ?? '',
      dogumTarihi: customerForm.faturaDogumTarihi ?? resolvedAddress.dogum_tarihi ?? '',
      adres: customerForm.faturaAdres ?? resolvedAddress.addressText ?? customerForm.adres ?? '',
      il: customerForm.faturaIl ?? resolvedAddress.il ?? customerForm.il ?? '',
      ilce: customerForm.faturaIlce ?? resolvedAddress.ilce ?? customerForm.ilce ?? '',
      email: customerForm.faturaEmail ?? resolvedAddress.email ?? customerForm.email ?? ''
    };
  }, [customerForm]);

  const billingAddressLine = useMemo(() => {
    const parts = [];
    if (billingInfo.adres) {
      parts.push(billingInfo.adres);
    }
    const cityParts = [billingInfo.ilce, billingInfo.il].filter(Boolean).join(', ');
    if (cityParts) {
      parts.push(cityParts);
    }
    return parts.join(' • ');
  }, [billingInfo.adres, billingInfo.il, billingInfo.ilce]);

  useEffect(() => {
    window.scrollTo(0, 0);

    // Kullanıcı giriş yapmışsa bakiye bilgisini al
    if (isLoggedIn && total) {
      fetchBalanceAndDistribution(total);
    }
  }, []);

  // Bakiye ve ödeme dağılımını getir
  const fetchBalanceAndDistribution = async (amount) => {
    try {
      const token = localStorage.getItem('token');
      const tkn = localStorage.getItem('tkn');

      const balanceResponse = await fetch(`${getApiUrl()}/calculate_balance_usage`, {
        method: 'POST',
        headers: {
          'Authorization': `${token}`,
          'Tkn': `${tkn}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ amount: amount })
      });

      const balanceData = await balanceResponse.json();
      if (balanceData.success) {
        setBalanceInfo(balanceData);
        setPaymentDistribution(balanceData.will_use);
      }
    } catch (error) {
      console.error('Bakiye bilgisi alınırken hata:', error);
    }
  };

  useEffect(() => {
    if (!isLoggedIn) {
      setIsEditingInvoice(true);
      setEditedInvoiceData(prev => {
        if (Object.keys(prev || {}).length > 0) {
          return prev;
        }
        return {
          ad: '',
          soyad: '',
          tckimlik: '',
          telefon: '',
          dogumTarihi: '',
          adres: '',
          email: ''
        };
      });
    }
  }, [isLoggedIn]);

  // EFT verilerini getir
  useEffect(() => {
    if (orderCreated) {
      const fetchEftData = async () => {
        try {
          const response = await fetch(`${getApiUrl()}/get_eft`);
          const data = await response.json();
          if (data && data.length > 0) {
            setEftData(data[0]);
          }
        } catch (error) {
          showToast('EFT bilgileri şu anda alınamıyor', 'error')
          console.error('EFT bilgileri alınırken hata oluştu:', error);
        }
      };

      fetchEftData();
    }
  }, [orderCreated]);

  // Fatura Düzenleme Fonksiyonları
  const handleEditClick = () => {
    setEditedInvoiceData({
      ad: billingInfo.ad,
      soyad: billingInfo.soyad,
      tckimlik: billingInfo.tckimlik,
      telefon: billingInfo.telefon,
      dogumTarihi: billingInfo.dogumTarihi,
      adres: billingInfo.adres,
      email: billingInfo.email
    });
    setIsEditingInvoice(true);
  };

  const handleInvoiceChange = (e) => {
    const { name, value } = e.target;

    setEditedInvoiceData(prev => ({
      ...prev,
      [name]: value
    }));

    const mappedKey = invoiceFieldMapping[name];
    if (mappedKey) {
      setCustomerForm(prev => ({
        ...(prev || {}),
        [mappedKey]: value
      }));
    }
  };

  const handlePhoneChange = (e) => {
    const { value } = e.target;
    const sanitizedValue = value.replace(/[^0-9]/g, '');
    const maxLength = selectedCountry.mobile_number_length || 15;

    if (sanitizedValue.length > maxLength) {
      return;
    }

    setEditedInvoiceData(prev => ({
      ...prev,
      telefon: sanitizedValue
    }));

    const mappedKey = invoiceFieldMapping['telefon'];
    if (mappedKey) {
      setCustomerForm(prev => ({
        ...(prev || {}),
        [mappedKey]: sanitizedValue
      }));
    }
  };

  const handleSaveInvoice = () => {
    if (!editedInvoiceData.ad || !editedInvoiceData.soyad || !editedInvoiceData.tckimlik || !editedInvoiceData.telefon || !editedInvoiceData.adres) {
      showToast('Lütfen zorunlu alanları doldurunuz.', 'error');
      return;
    }

    if (!validateTCKN(editedInvoiceData.tckimlik)) {
      showToast('Geçersiz TC Kimlik Numarası.', 'error');
      return;
    }

    setCustomerForm(prev => ({
      ...prev,
      faturaAd: editedInvoiceData.ad,
      faturaSoyad: editedInvoiceData.soyad,
      faturaTckimlik: editedInvoiceData.tckimlik,
      faturaTelefon: editedInvoiceData.telefon,
      faturaDogumTarihi: editedInvoiceData.dogumTarihi,
      faturaAdres: editedInvoiceData.adres,
      faturaEmail: editedInvoiceData.email
    }));
    setIsEditingInvoice(false);
    showToast('Fatura bilgileri güncellendi.', 'success');
  };

  const handleCancelEdit = () => {
    setIsEditingInvoice(false);
    setEditedInvoiceData({});
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    try {
      const referansKodu = location.state?.referansKodu || '';

      if (!customerForm) {
        showToast('Fatura bilgileri bulunamadı. Lütfen adres adımına geri dönün.', 'error');
        setLoading(false);
        return;
      }

      if (
        !billingInfo.ad ||
        !billingInfo.soyad ||
        !billingInfo.adres ||
        !billingInfo.dogumTarihi ||
        !billingInfo.tckimlik ||
        !billingInfo.telefon
      ) {
        showToast('Fatura bilgileri eksik. Lütfen bilgileri düzenleyiniz.', 'error');
        setLoading(false);
        return;
      }

      const tcknDigits = billingInfo.tckimlik.replace(/\D/g, '');
      const phoneDigits = billingInfo.telefon.replace(/\D/g, '');
      const expectedLength = selectedCountry.mobile_number_length;

      if (!validateTCKN(tcknDigits)) {
        showToast('Fatura TC Kimlik bilgisinde sorun var. Lütfen kontrol ediniz.', 'error');
        setLoading(false);
        return;
      }

      if (phoneDigits.length !== expectedLength) {
        showToast(`Telefon numarası ${expectedLength} haneli olmalıdır. Lütfen kontrol ediniz.`, 'error');
        setLoading(false);
        return;
      }

      const updatedCustomerForm = {
        ...customerForm,
        faturaAd: (billingInfo.ad || '').trim(),
        faturaSoyad: (billingInfo.soyad || '').trim(),
        faturaAdres: (billingInfo.adres || '').trim(),
        faturaDogumTarihi: billingInfo.dogumTarihi,
        faturaTckimlik: tcknDigits,
        faturaTelefon: phoneDigits,
        faturaIl: billingInfo.il ?? '',
        faturaIlce: billingInfo.ilce ?? '',
        faturaEmail: billingInfo.email ?? ''
      };

      fbq('track', 'InitiateCheckout');

      const eftPaymentData = {
        musteriForm: updatedCustomerForm,
        odenecek_tutar: total,
        referans_kodu: referansKodu,
        payment_type: 'eft',
        isEftPayment: true
      };

      const response = await processPayment(eftPaymentData, showToast);

      if (response.success) {
        setOrderCreated(true);
        setOrderNumber(response.orderId || 'EFT-' + Date.now());
        setOrderAmount(response.price || 0);

        // EFT siparişi başarıyla oluşturulduğunda share_ref çerezini sil
        document.cookie = "share_ref=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";

        setLoading(false);
      } else {
        throw new Error(response.message || 'EFT/Havale sipariş kaydı başarısız');
      }
    } catch (error) {
      setErrors(prev => ({
        ...prev,
        submit: error.message || 'Sipariş oluşturulurken bir hata oluştu'
      }));
      setLoading(false);
    }
  };

  // Metin kopyalama fonksiyonu
  const copyToClipboard = (text, label) => {
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);

      textArea.focus();
      textArea.select();

      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);

      if (successful) {
        setCopySuccess(`${label} kopyalandı!`);
        setTimeout(() => setCopySuccess(''), 2000);
      } else {
        navigator.clipboard.writeText(text)
          .then(() => {
            setCopySuccess(`${label} kopyalandı!`);
            setTimeout(() => setCopySuccess(''), 2000);
          })
          .catch(err => {
            setCopySuccess('Kopyalama başarısız!');
            console.error('Kopyalama hatası:', err);
          });
      }
    } catch (err) {
      setCopySuccess('Kopyalama başarısız!');
      console.error('Kopyalama hatası:', err);
    }
  };

  // EFT ödeme bildirimi fonksiyonu
  const handleConfirmEftPayment = async () => {
    try {
      if (!orderNumber) {
        console.error('Sipariş numarası bulunamadı');
        showToast('Sipariş numarası bulunamadı. Lütfen tekrar deneyin.', 'error');
        return;
      }
      setLoading(true);
      const result = await confirmEftPayment(orderNumber, orderAmount, showToast);

      if (result.success) {
        // Başarılı işlem
      }
    } catch (error) {
      console.error('EFT ödeme bildirimi yapılırken hata:', error);
      showToast('EFT bildirimi yapılırken bir hata oluştu.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // EFT ödeme onaylama için useEffect
  useEffect(() => {
    if (orderNumber && orderCreated && orderAmount > 0) {
      handleConfirmEftPayment();
    }
  }, [orderNumber, orderCreated, orderAmount]);

  const handlePaymentComplete = () => {
    setIsPaymentCompleteModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsPaymentCompleteModalOpen(false);
    navigate('/');
  };

  if (loading) return <div className='loader-info'>
    <p className='m-0'>İşlem alınıyor...</p>
    <Loader />
  </div>;

  return (
    <>
      <div className='payment-title'>
        <h2 className='my-3'>EFT/Havale ile Ödeme</h2>

        {/* Fatura Bilgileri Formu - Sipariş oluşturulmuşsa gösterme */}
        {!orderCreated && (
          <div>
            <div>
              <div className="payment-information-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3>Fatura Bilgileri</h3>
                {isLoggedIn && !isEditingInvoice && (
                  <button
                    onClick={handleEditClick}
                    style={{
                      padding: '5px 10px',
                      fontSize: '0.9rem',
                      backgroundColor: '#f0f0f0',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Düzenle
                  </button>
                )}
              </div>
              <div className='invoice-group'>
                <p className="invoice-info-text"><strong>Ücretin gönderildiği hesap</strong> fatura bilgisi ile <strong>uymalıdır</strong>.
                  Aksi takdirde <strong>siparişiniz iptal edilecektir</strong></p>

                <p className='invoice-info-text'>Son ekrana gelene kadar ürün fiyatları kurlar ile birlikte <strong>güncellenmektedir.</strong> Son ulaştığınız ekrandaki fiyat <strong>10 dakika</strong> için geçerlidir.</p>

                <p className='invoice-info-text'><strong>10 dakika</strong> içinde ödeme <strong>gerçekleştirilmezse</strong> fiyatlar <strong>anlık</strong> kurla beraber güncellenecektir</p>
              </div>
            </div>
            <div className="invoice-information">
              <div className="invoice-form p-md-4 p-2">
                {isEditingInvoice ? (
                  <>
                    <div className="d-flex flex-md-row flex-column gap-2">
                      <div className="form-group">
                        <label>Ad</label>
                        <input
                          type="text"
                          name="ad"
                          value={editedInvoiceData.ad || ''}
                          onChange={handleInvoiceChange}
                          className="form-control"
                        />
                      </div>
                      <div className="form-group">
                        <label>Soyad</label>
                        <input
                          type="text"
                          name="soyad"
                          value={editedInvoiceData.soyad || ''}
                          onChange={handleInvoiceChange}
                          className="form-control"
                        />
                      </div>
                    </div>
                    <div className="d-flex flex-md-row flex-column gap-2">
                      <div className='form-group'>
                        <label>TC Kimlik No</label>
                        <input
                          type="text"
                          name="tckimlik"
                          value={editedInvoiceData.tckimlik || ''}
                          onChange={handleInvoiceChange}
                          maxLength="11"
                          className="form-control"
                          style={{ marginBottom: '5px' }}
                        />
                      </div>
                      <div className="form-group">
                        <label>Telefon</label>
                        <div className='d-flex gap-2'>
                          <div>
                            <CountrySelector
                              selectedCountry={selectedCountry}
                              onSelect={(country) => {
                                setSelectedCountry(country);
                                setEditedInvoiceData(prev => ({ ...prev, telefon: '' }));
                              }}
                              countries={countryCodes}
                            />
                          </div>
                          <input
                            type="tel"
                            name="telefon"
                            value={editedInvoiceData.telefon || ''}
                            onChange={handlePhoneChange}
                            className="form-control"
                            placeholder={selectedCountry.mobile_number_length ? `${selectedCountry.mobile_number_length} haneli` : "Telefon"}
                          />
                        </div>
                      </div>
                    </div>
                    <div className='d-flex flex-md-row flex-column gap-2'>
                      <div className="form-group">
                        <label>Doğum Tarihi</label>
                        <input
                          type="date"
                          name="dogumTarihi"
                          value={editedInvoiceData.dogumTarihi || ''}
                          onChange={handleInvoiceChange}
                          className="form-control"
                        />
                      </div>
                      <div className="form-group">
                        <label>Email</label>
                        <input
                          type="email"
                          name="email"
                          value={editedInvoiceData.email || ''}
                          onChange={handleInvoiceChange}
                          className="form-control"
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Adres</label>
                      <textarea
                        name="adres"
                        value={editedInvoiceData.adres || ''}
                        onChange={handleInvoiceChange}
                        className="form-control"
                        rows="3"
                      />
                    </div>
                    {isLoggedIn && (
                      <div className="d-flex gap-2 justify-content-end">
                        <button
                          type="button"
                          onClick={handleCancelEdit}
                          className="btn btn-bikuyum"
                        >
                          İptal
                        </button>
                        <button
                          type="button"
                          onClick={handleSaveInvoice}
                          className="btn btn-bikuyum"
                        >
                          Kaydet
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="d-flex flex-md-row flex-column gap-2">
                      <div className="form-group">
                        <label>Ad Soyad</label>
                        <p className="readonly-text">
                          {`${billingInfo.ad || ''} ${billingInfo.soyad || ''}`.trim() || '-'}
                        </p>
                      </div>
                      <div className="form-group">
                        <label>TC Kimlik No</label>
                        <p className="readonly-text">
                          {billingInfo.tckimlik || '-'}
                        </p>
                      </div>
                    </div>
                    <div className="d-flex flex-md-row flex-column gap-2">
                      <div className="form-group">
                        <label>Telefon</label>
                        <p className="readonly-text">
                          {formatPhoneForDisplay(billingInfo.telefon)}
                        </p>
                      </div>
                      <div className="form-group">
                        <label>Doğum Tarihi</label>
                        <p className="readonly-text">
                          {formatDateForDisplay(billingInfo.dogumTarihi)}
                        </p>
                      </div>
                    </div>
                    {billingInfo.email && (
                      <div className="form-group">
                        <label>Email</label>
                        <p className="readonly-text">{billingInfo.email}</p>
                      </div>
                    )}
                    <div className="form-group">
                      <label>Adres</label>
                      <p className="readonly-text">
                        {billingAddressLine || '-'}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* EFT Ödeme Bilgileri */}
        <div className="payment-information-section">
          <div className="">
            <div>
              {!orderCreated ? (
                // Sipariş oluşturulmadan önce
                <div className='eft-confirm-container'>
                  {/* Bakiye Kullanım Bilgisi */}
                  {balanceInfo && paymentDistribution && typeof paymentDistribution.total === 'number' && paymentDistribution.total > 0 && (
                    <div className="balance-usage-info" style={{
                      backgroundColor: '#e3f2fd',
                      border: '2px solid #2196f3',
                      borderRadius: '8px',
                      padding: '15px',
                      marginBottom: '20px'
                    }}>
                      <h4 className="text-center" style={{ color: '#1976d2', margin: '0 0 10px 0', fontSize: '16px' }}>
                        💰 Bakiye Kullanım Bilgisi
                      </h4>
                      <div style={{ fontSize: '14px', color: '#424242' }}>
                        <p style={{ margin: '5px 0' }}>
                          <strong>Toplam Tutar:</strong> {total ? total.toFixed(2) : '0.00'} TL
                        </p>
                        <p style={{ margin: '5px 0' }}>
                          <strong>Bakiyenizden Kullanılacak:</strong> <span style={{ color: '#2e7d32', fontWeight: 'bold' }}>{(paymentDistribution.total || 0).toFixed(2)} TL</span>
                        </p>
                        <p style={{ margin: '5px 0' }}>
                          <strong>EFT ile Ödenecek:</strong> <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>{(total - (paymentDistribution.total || 0)).toFixed(2)} TL</span>
                        </p>
                      </div>
                      {balanceInfo.balance_covers_full_amount && (
                        <p style={{
                          margin: '10px 0 0 0',
                          fontSize: '13px',
                          color: '#2e7d32',
                          fontWeight: 'bold',
                          padding: '8px',
                          backgroundColor: '#e8f5e9',
                          borderRadius: '4px'
                        }}>
                          ✅ Siparişiniz tamamen bakiyenizle ödenecektir!
                        </p>
                      )}
                    </div>
                  )}

                  <p>Siparişinizi oluşturmak için aşağıdaki butona tıklayın. Sonrasında ödeme bilgileriniz görüntülenecektir.</p>
                  <button
                    onClick={handlePaymentSubmit}
                    className="eft-confirm-button"
                    disabled={loading}
                  >
                    {loading ? 'İşlem Yapılıyor...' : 'Siparişi Oluştur'}
                  </button>
                  {errors.submit && (
                    <div className="eft-error-message">
                      <p>{errors.submit}</p>
                    </div>
                  )}
                </div>
              ) : orderAmount === 0 ? (
                // Sipariş bakiyeyle tamamen ödendi
                <div className="balance-payment-success-container">
                  <div className="success-animation">
                    <div className="success-checkmark">
                      <div className="check-icon">
                        <span className="check-mark">✓</span>
                      </div>
                    </div>
                  </div>

                  <h2 className="success-title">Ödeme Başarılı!</h2>
                  <p className="success-subtitle">
                    Siparişiniz başarıyla alındı ve hazırlanmaya başlandı.
                  </p>

                  <div className="order-success-card">
                    <div className="success-details">
                      <div className="detail-row">
                        <span className="detail-label">Sipariş Numarası</span>
                        <span className="detail-value">#{orderNumber}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Ödenen Tutar</span>
                        <span className="detail-value amount">{total ? total.toFixed(2) : '0.00'} TL</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Ödeme Yöntemi</span>
                        <span className="detail-value"><span className="method-badge">💰 Bakiye</span></span>
                      </div>
                    </div>

                    <div className="success-info">
                      <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                        <path d="M10 0C5.58 0 2 3.58 2 8C2 12.42 5.58 16 10 16C14.42 16 18 12.42 18 8C18 3.58 14.42 0 10 0ZM10 14C6.68 14 4 11.32 4 8C4 4.68 6.68 2 10 2C13.32 2 16 4.68 16 8C16 11.32 13.32 14 10 14ZM12.8 6.2L8.8 10.2L7.2 8.6L5.8 10L8.8 13L14.2 7.6L12.8 6.2Z" fill="currentColor" />
                      </svg>
                      <p>Sipariş durumunuzu hesabınızdan takip edebilirsiniz.</p>
                    </div>
                  </div>

                  <button
                    onClick={() => navigate('/')}
                    className="btn-return-home"
                  >
                    Ana Sayfaya Dön
                  </button>
                </div>
              ) : (
                // Sipariş oluşturulduktan sonra
                <div>
                  <h3>EFT/Havale ile Ödeme - Sipariş Onayınız</h3>
                  <p>Siparişiniz başarıyla oluşturuldu. Aşağıdaki banka hesabına ödemenizi yapınız. Ödeme sonrası siparişiniz onaylanarak işleme alınacaktır.</p>

                  <div className="order-details">
                    <p>
                      <strong>Sipariş Numarası:</strong> {orderNumber}
                    </p>
                    <p><strong>Ödenecek Tutar:</strong> {orderAmount ? orderAmount.toFixed(2) : '0.00'} TL</p>
                    <p className='order-details-info'>
                      10 Dakika içerisinde ödeme yapmazsanız fiyat güncellenecektir.
                    </p>
                  </div>

                  <div className="bank-account-info">
                    <div>
                      <p><strong>Banka:</strong></p>
                      <p>{eftData ? eftData.bank : 'Yükleniyor...'}</p>
                    </div>
                    <div>
                      <p><strong>IBAN:</strong></p>
                      <p>
                        {eftData ? eftData.iban : 'Yükleniyor...'}
                        <button
                          className="copy-button"
                          onClick={() => copyToClipboard(eftData ? eftData.iban : '', 'IBAN')}
                        >
                          <span>📋</span>
                        </button>
                      </p>
                    </div>
                    <div>
                      <p><strong>Hesap Sahibi:</strong></p>
                      <p>
                        {eftData ? eftData.name : 'Yükleniyor...'}
                        <button
                          className="copy-button"
                          onClick={() => copyToClipboard(eftData ? eftData.name : '', 'Hesap sahibi')}
                        >
                          <span>📋</span>
                        </button>
                      </p>
                    </div>
                    <div>
                      <p><strong>Açıklama:</strong></p>
                      <p>
                        {orderNumber} Nolu Sipariş
                        <button
                          className="copy-button"
                          onClick={() => copyToClipboard(`${orderNumber} Nolu Sipariş`, 'Açıklama')}
                        >
                          <span>📋</span>
                        </button>
                      </p>
                    </div>
                  </div>

                  {copySuccess && <div className="copy-success-message">{copySuccess}</div>}

                  <div className="eft-important-notice">
                    <div className="siparis-no-uyarisi">
                      <p>⚠️ <strong>ÖNEMLİ:</strong> Havale/EFT yaparken açıklama kısmına mutlaka <strong>{orderNumber}</strong> yazınız.</p>
                    </div>
                  </div>

                  <button
                    onClick={handlePaymentComplete}
                    className="payment-complete-button btn btn-outline-primary d-block mx-auto"
                  >
                    Ödemeyi Yaptım
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={isPaymentCompleteModalOpen}
        onRequestClose={handleCloseModal}
        contentLabel="Ödeme Bildirimi"
        className="ReactModal__Content"
        overlayClassName="ReactModal__Overlay"
      >
        <div className="payment-complete-modal text-center">
          <h2>Teşekkürler!</h2>
          <strong>Ödeme bildiriminiz alınmıştır. Kontroller sağlandıktan sonra siparişiniz onaylanacaktır.</strong>
          <button onClick={handleCloseModal} className="modal-close-button btn btn-outline-primary d-block mx-auto mt-3">
            Ana Sayfaya Dön
          </button>
        </div>
      </Modal>
    </>
  );
};

export default EftPayment;
